import { Button, Form, Input, Modal, Upload } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { FormInstance } from "antd/es/form";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import {
  type CasinoConfigGameRow,
  formatGameIdForModalSubtitle,
} from "../../../../../utils/game-utils/gamesUtils";
import type { AdminTag } from "../../../../../queries/casino-configuration/games/fetchAdminTags";
import { useCurationDisplayName } from "../../../../../hooks/useCurationDisplayName";

const ACCEPTED = ["image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export type EditCasinoGameFormValues = {
  name: string;
};

type EditCasinoGameModalProps = {
  open: boolean;
  game: CasinoConfigGameRow | null;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  uploading?: boolean;
  form: FormInstance<EditCasinoGameFormValues>;
  previewUrl: string;
  currentIconUrl: string;
  fallbackIcon: string;
  onBeforeUpload: (file: File) => boolean;
  onRemoveIcon: () => void;
  canUploadImages?: boolean;
  onManageTags?: () => void;
  adminTags?: AdminTag[];
};

function EditCasinoGameModal({
  open,
  game,
  onCancel,
  onSave,
  saving,
  uploading = false,
  form,
  previewUrl,
  currentIconUrl,
  fallbackIcon,
  onBeforeUpload,
  onRemoveIcon,
  canUploadImages = true,
  onManageTags,
  adminTags = [],
}: EditCasinoGameModalProps) {
  const { getName: getTagDisplayName } = useCurationDisplayName();
  const subtitle = game
    ? AppStrings.CASINO_CONFIG_GAMES_EDIT_GAME_ID.replace(
        "{id}",
        formatGameIdForModalSubtitle(game),
      )
    : "";

  return (
    <Modal
      open={open}
      rootClassName="casino-games-modal-root"
      title={
        <span className="casino-games-modal-title">
          {AppStrings.CASINO_CONFIG_GAMES_EDIT_MODAL_TITLE}
        </span>
      }
      className="casino-games-modal"
      closeIcon={<CloseOutlined />}
      onCancel={onCancel}
      onOk={onSave}
      okText={AppStrings.CASINO_CONFIG_GAMES_ACTION_SAVE}
      cancelText={AppStrings.CASINO_CONFIG_GAMES_ACTION_CANCEL}
      confirmLoading={saving}
      width={560}
      okButtonProps={{
        className: "casino-games-primary-btn",
        disabled: uploading,
        loading: saving || uploading,
      }}
      cancelButtonProps={{ className: "casino-games-outline-btn" }}
    >
      {subtitle ? (
        <div className="casino-games-edit-game-id">{subtitle}</div>
      ) : null}
      <Form form={form} layout="vertical">
        <Form.Item
          className="casino-games-form-item"
          label={
            <span className="casino-games-form-label">
              {AppStrings.CASINO_CONFIG_GAMES_FORM_NAME_LABEL}
            </span>
          }
          name="name"
          rules={[
            {
              required: true,
              message: AppStrings.CASINO_CONFIG_GAMES_FORM_NAME_REQUIRED,
            },
          ]}
        >
          <Input
            className="casino-games-modal-input"
            placeholder={AppStrings.CASINO_CONFIG_GAMES_FORM_NAME_PLACEHOLDER}
            maxLength={120}
          />
        </Form.Item>

        <div className="casino-games-icon-title">
          {AppStrings.CASINO_CONFIG_GAMES_FORM_ICON_LABEL}
        </div>
        <div className="casino-games-config-upload">
          <img
            className="casino-games-config-upload-preview"
            data-testid="casino-config-game-icon-preview"
            src={previewUrl || currentIconUrl || fallbackIcon}
            alt=""
          />
          <div>
            <div className="casino-games-config-upload-actions">
              <Upload
                id="casino-config-game-upload-input"
                showUploadList={false}
                beforeUpload={onBeforeUpload}
                accept=".png,.webp,image/png,image/webp"
                disabled={!canUploadImages}
              >
                <Button
                  className="casino-games-outline-btn"
                  disabled={!canUploadImages}
                >
                  {AppStrings.CASINO_CONFIG_GAMES_FORM_REPLACE}
                </Button>
              </Upload>
              <Button
                className="casino-games-outline-btn"
                disabled={!canUploadImages}
                onClick={onRemoveIcon}
              >
                {AppStrings.CASINO_CONFIG_GAMES_FORM_DELETE}
              </Button>
            </div>
            <div className="casino-games-config-upload-note">
              {AppStrings.CASINO_CONFIG_GAMES_FORM_ICON_HINT}
            </div>
          </div>
        </div>

        <div className="casino-games-tags-section">
          <div className="casino-games-tags-header">
            <span className="casino-games-form-label">
              {AppStrings.CASINO_CONFIG_GAMES_COL_TAGS}
            </span>
            {onManageTags ? (
              <button
                type="button"
                className="casino-games-tags-manage-btn"
                onClick={onManageTags}
              >
                {AppStrings.CASINO_CONFIG_GAMES_TAG_MANAGE}
              </button>
            ) : null}
          </div>
          {game?.tags && game.tags.length > 0 ? (
            <div className="casino-games-tags-list">
              {game.tags.map((tagName) => {
                const tagObj = adminTags.find(
                  (t) =>
                    t.id === tagName ||
                    t.name.toLowerCase() === tagName.toLowerCase(),
                );
                const tagColor = tagObj?.color || "#9ea2b8";
                const tagLabel = tagObj ? getTagDisplayName(tagObj) : tagName;
                return (
                  <span
                    key={tagName}
                    className="casino-games-edit-tag-pill"
                    style={{
                      background: `${tagColor}22`,
                      borderColor: tagColor,
                      color: tagColor,
                    }}
                    title={
                      tagObj && tagLabel !== tagObj.name ? tagObj.name : undefined
                    }
                  >
                    {tagLabel}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="casino-games-tags-none">
              {AppStrings.CASINO_CONFIG_GAMES_TAG_NO_TAGS}
            </span>
          )}
        </div>
      </Form>
    </Modal>
  );
}

export { ACCEPTED as EDIT_GAME_ACCEPTED_TYPES, MAX_BYTES as EDIT_GAME_MAX_BYTES };
export default EditCasinoGameModal;
